/**
 * EgressTemplate · STEP OVERLAY-2A · Egress Overlay Foundation
 *
 * LiveKit RoomComposite 공식 Custom Web Template.
 * LiveKit Egress Chromium headless 가 이 페이지를 로드 → room 참여 →
 * 녹화된 영상 (비디오 트랙 + 이 페이지의 Overlay HTML) 을 RTMP 로 송출.
 *
 * 이번 STEP 범위 (OVERLAY-2A — Foundation):
 *   · Active Product 1종만 Overlay 로 렌더 (이미지 · 상품명 · 가격).
 *   · 9:16 / 16:9 두 canvas 모두 깨지지 않는 CSS.
 *   · Failure Isolation: data/API 오류 시 Overlay 를 조용히 hide. 영상은 계속 송출.
 *   · 기존 Browser UI (LiveKitViewer / SellerLiveConsole) 완전 무변경.
 *
 * NOT in scope:
 *   · Notice · Timer · Purchase/Follower/Inventory Alert · QR · Ticker.
 *   · Drag · Resize · Preset · Layout Lock · State Machine · AI Assistant.
 *
 * LiveKit 가 제공하는 URL params:
 *   ?url=<wss://..>&token=<viewerToken>&room=<room_name>&layout=<layout>
 * 우리가 base URL 에 포함한 params:
 *   ?session_id=... (path) · ?orientation=portrait|landscape (query)
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Room, RoomEvent, Track } from 'livekit-client';
import SafeImg from '@/components/SafeImg';
import { OverlayQrCard, OverlaySizeTableCard, OverlayNoticeImage, OverlayNoticeCard, OverlayTimerCard, sizeTableHasData, LIGHT } from '@/components/live/OverlayExtras';
import { timerViewOf } from '@/lib/timerLocal';
import OverlayFreeBox from '@/components/live/OverlayFreeBox';
import LivePresetOverlay from '@/components/live/LivePresetOverlay';
import { getRect, orientKey, baseFrac, cardOrientation } from '@/lib/overlayLayout';
import { attachRemoteTrack, detachRemoteTrack } from '@/lib/egressTracks';

const CTX_POLL_MS = 3000;
// STEP OVERLAY-2B-RT · 서버 Data Channel realtime push 수신 후 Renderer 가
// 이 TTL 안에 polling 결과를 "덮어쓰지" 않도록 가드 (polling 지연 /
// 동일 상품 이중 변경 시 Realtime 결과를 우선 유지). polling 은 TTL 경과
// 또는 Realtime 미수신 시 그대로 Recovery path 로 동작한다.
const RT_PRIORITY_TTL_MS = 2500;

const _won = (n) => `${(Number(n) || 0).toLocaleString('ko-KR')}원`;

// 공식 LiveKit CustomBaseURL 가 제공하는 query param 추출.
function readLkParams() {
  try {
    const sp = new URLSearchParams(window.location.search);
    return {
      lkUrl: sp.get('url') || '',
      lkToken: sp.get('token') || '',
      lkRoom: sp.get('room') || '',
    };
  } catch (_e) {
    return { lkUrl: '', lkToken: '', lkRoom: '' };
  }
}

const EgressTemplate = () => {
  const { sessionId } = useParams();
  const [searchParams] = useSearchParams();
  const orientation = (searchParams.get('orientation') || 'landscape').toLowerCase() === 'portrait'
    ? 'portrait' : 'landscape';
  // STEP OVERLAY-UX-FINAL-1 · Seller Preview mode (REUSE this template).
  // `?preview=1` 로 embed 되면: (a) stage background 는 transparent,
  // (b) <video> 는 hidden — parent 의 camera monitor 를 가리지 않도록.
  // LiveKit room 연결 + START_RECORDING 신호는 lkUrl/lkToken 유무로 이미 gate.
  // 즉 preview 모드는 polling fallback (/live-context) 만으로 overlay 렌더.
  const preview = searchParams.get('preview') === '1';
  // LIVEMOA-LIVE-TEST-PREVIEW-INTEGRATION-1 · Studio 송출 미리보기 · live-context 대신 parent postMessage 데이터 (preview 전용).
  const parentMode = preview && searchParams.get('source') === 'parent'
    && typeof window !== 'undefined' && window.parent !== window;
  const parentCtxRef = useRef(null);
  const [parentTick, setParentTick] = useState(0);
  useEffect(() => {
    if (!parentMode) return undefined;
    const on = (e) => {
      if (e.origin !== window.location.origin || e.source !== window.parent || e.data?.type !== 'livemoa-preview-ctx') return;
      parentCtxRef.current = e.data.ctx || {};
      setParentTick((t) => t + 1);
    };
    window.addEventListener('message', on);
    window.parent.postMessage({ type: 'livemoa-preview-ready' }, window.location.origin);
    return () => window.removeEventListener('message', on);
  }, [parentMode]);

  // STEP STUDIO-MOBILE-LIVE-WHITE-SCREEN-FIX-1 · 2026-10-11
  //   RCA: Seller Preview 모드에서 EgressTemplate 가 iframe 안으로 로드될 때,
  //     stageStyle.background = 'transparent' 는 외곽 <div> 만 투명하게 만들고,
  //     iframe document 의 <html>/<body> 는 index.css 의 `--background: 100%` →
  //     bg-background(흰색) 그대로 상속 → 모바일(Android/Samsung)에서 LIVE 시작
  //     순간 iframe 전체가 흰 사각형으로 카메라 video 를 가린다.
  //   PC 는 2026-10-08 WHITE-PREVIEW-FIX 로 iframe 을 `lg:hidden` 처리하여
  //     이슈를 회피했으나, 모바일은 미해결 상태였음.
  //   FIX: preview=1 일 때만 iframe 내부 <html>/<body> 를 transparent 로 강제.
  //     실 LiveKit Egress Chromium (preview=0) 은 기존대로 #000 유지 — 송출 송출
  //     품질/Overlay 합성 영향 0.
  useEffect(() => {
    if (!preview || typeof document === 'undefined') return;
    const prevHtmlBg = document.documentElement.style.background;
    const prevBodyBg = document.body ? document.body.style.background : '';
    try { document.documentElement.style.background = 'transparent'; } catch { /* ignore */ }
    try { if (document.body) document.body.style.background = 'transparent'; } catch { /* ignore */ }
    return () => {
      try { document.documentElement.style.background = prevHtmlBg; } catch { /* ignore */ }
      try { if (document.body) document.body.style.background = prevBodyBg; } catch { /* ignore */ }
    };
  }, [preview]);

  const [activeProduct, setActiveProduct] = useState(null);
  const [display, setDisplay] = useState(null);    // STEP OVERLAY-2C · null → DEFAULT fallback
  const [variants, setVariants] = useState([]);    // STEP OVERLAY-2C · whitelisted
  const [selfNotice, setNotice] = useState(null);
  // REAL-CONTROL-INTEGRATION-1 · 외부송출용 공지 (자사라이브 공지와 데이터 분리 · Egress 영상에만 표시).
  const [extNotice, setExtNotice] = useState(null);
  const [timer, setTimer] = useState(null);        // STEP OVERLAY-2D · event timer
  const [nowTick, setNowTick] = useState(() => Date.now());  // 1Hz local tick (clock drift)
  const timerRtTsRef = useRef(0);
  // STEP OVERLAY-SIZE-TABLE-1 · Product 상세 사이즈표 Overlay 상태.
  //   · DB SoT: seller_products.size_table (POST/PUT /seller/products 시 저장).
  //   · active_product.size_table 로 전달 (ap_payload + live-context recovery).
  //   · active_product_display.size_table_visible === true 일 때만 render.
  //   · 신규 realtime topic 없음 · 신규 collection 없음.
  const [sizeTable, setSizeTable] = useState(null);
  // STEP OVERLAY-2J-QR · dynamic QR state (persistent until expires_at).
  // Payload: {product_id, url_path, expires_at}. URL assembly 는 Renderer 측 (origin + url_path).
  const [qrState, setQrState] = useState(null);
  // STEP STUDIO-OVERLAY-DRAG-RESIZE-1 · 자유 배치 (live-context.overlay_layout · 없으면 기존 위치).
  const [layout, setLayout] = useState(null);
  const qrRtTsRef = useRef(0);
  // STEP OVERLAY-2G-FOLLOWER · common transient Event Queue (FIFO · in-renderer).
  // 공통 모델: follower + future purchase + other social_proof events 를 하나의
  // Queue 에서 FIFO 로 순차 표시. Server 는 1회 push, Renderer 는 duration 끝날
  // 때마다 자동 dequeue · 동일 proof_id/event_id 중복 자동 dedupe.
  const [eventQueue, setEventQueue] = useState([]);
  const [currentEvent, setCurrentEvent] = useState(null);
  const seenEventIdsRef = useRef(new Set());
  const EVENT_QUEUE_MAX = 30;             // 지시 §20 · 과도 방지
  const EVENT_DEFAULT_DURATION_MS = 4000; // 지시 §13 · 기본 4s
  const [connected, setConnected] = useState(false);
  const videoRef = useRef(null);
  const audioBoxRef = useRef(null);
  const roomRef = useRef(null);
  const mountedRef = useRef(true);
  // STEP OVERLAY-2B-RT · Realtime 이 state 를 최근에 교체한 시각.
  // polling tick 이 TTL 이내에 도착하면 polling 결과를 그대로 사용하지 않고
  // Realtime 상태를 유지한다 (eventual consistency 는 TTL 이후 자동 수렴).
  const rtLastTsRef = useRef(0);
  // STEP OVERLAY-2B-NOTICE · Notice 전용 TTL guard (상품과 독립).
  // Realtime 로 notice 가 바뀐 직후 polling 이 stale 값으로 되돌리는 것을 막는다.
  const noticeRtTsRef = useRef(0);

  // ── Backend base URL (same host as this template is served from) ─────────
  const apiBase = useMemo(() => {
    const env = process.env.REACT_APP_BACKEND_URL;
    if (env) return env.replace(/\/+$/, '');
    try { return window.location.origin; } catch (_e) { return ''; }
  }, []);

  // ── STEP OVERLAY-REAL-EGRESS-FAIL-2 · LiveKit Custom Web Template signal ──
  // ROOT CAUSE (REAL LiveKit Cloud · egress list_egress 결과 "status=5 ABORTED,
  //  error=Start signal not received"):
  //   LiveKit Egress Chromium 은 Custom Web Template 을 로드한 뒤 stdout 으로
  //   `console.log('START_RECORDING')` 를 받아야 녹화를 시작한다.  이 신호가
  //   30~60초 안에 들어오지 않으면 LiveKit 가 egress 를 ABORT 처리한다 →
  //   RTMP 바이트 미송출 → YouTube streamStatus=inactive → `ingest_not_ready`
  //   → UI "YouTube ingest 준비 지연" 토스트.
  //   공식 LiveKit Egress Custom Template Protocol:
  //     · 녹화 시작 가능: console.log('START_RECORDING')
  //     · 녹화 종료:    console.log('END_RECORDING')
  //   mount 즉시 1회만 보낸다 (idempotent · HMR·remount 는 모듈 flag 로 가드).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.__LIVEKIT_EGRESS_START_EMITTED__) return;
    const { lkUrl, lkToken } = readLkParams();
    // LiveKit 가 로드한 경우에만 신호 전송 (수동 QA 로컬 preview 는 skip).
    if (!lkUrl || !lkToken) return;
    window.__LIVEKIT_EGRESS_START_EMITTED__ = true;
    // 반드시 console.log 로 남겨 Chromium stdout 에 flush 되게 한다.
    // Chromium devtools protocol 이 이 라인을 캐치하는 구조.
    // eslint-disable-next-line no-console
    console.log('START_RECORDING');
    // Overlay 가 unmount / 페이지가 닫힐 때는 명시적으로 END_RECORDING 를 보내
    // LiveKit 가 녹화를 graceful 하게 마무리하게 한다 (abort 금지).
    const _onUnload = () => {
      try {
        // eslint-disable-next-line no-console
        console.log('END_RECORDING');
      } catch (_e) { /* ignore */ }
    };
    window.addEventListener('beforeunload', _onUnload);
    window.addEventListener('pagehide', _onUnload);
    return () => {
      try { window.removeEventListener('beforeunload', _onUnload); } catch (_e) {}
      try { window.removeEventListener('pagehide', _onUnload); } catch (_e) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── LiveKit room connect (subscribe-only · token comes from Egress URL) ──
  useEffect(() => {
    const { lkUrl, lkToken } = readLkParams();
    if (!lkUrl || !lkToken) {
      // LiveKit 가 직접 로드하지 않은 로컬 preview 접속일 때 — 영상은 없고
      // Overlay 렌더만 확인 가능 (수동 QA 시 유용). 오류 UI 금지.
      return;
    }
    const room = new Room({ adaptiveStream: false, dynacast: false });
    roomRef.current = room;
    const onTrackSubscribed = (track) => {
      try {
        attachRemoteTrack(track, { videoEl: videoRef.current, audioContainer: audioBoxRef.current, kinds: Track.Kind });
      } catch (_e) { /* failure isolation */ }
    };
    const onTrackUnsubscribed = (track) => {
      try { detachRemoteTrack(track); } catch (_e) { /* ignore */ }
    };
    // STEP OVERLAY-2B-RT / 2B-NOTICE · realtime data channel receiver.
    // malformed payload / unknown topic 은 silent drop. Renderer crash 금지.
    const onDataReceived = (payload, _participant, _kind, topic) => {
      try {
        if (!payload) return;
        const text = new TextDecoder('utf-8').decode(payload);
        if (!text) return;
        const msg = JSON.parse(text);
        const t = (topic || msg?.type || '').toString();
        // --- active_product ---
        if (t === 'active_product' || msg?.type === 'active_product') {
          const ap = msg && 'active_product' in msg ? msg.active_product : null;
          rtLastTsRef.current = Date.now();
          if (ap && typeof ap === 'object' && ap.product_id) {
            setActiveProduct(ap);
            // STEP OVERLAY-SIZE-TABLE-1 · ap payload 에 size_table 이 있으면 수신.
            // Overlay 실제 노출 여부는 display.size_table_visible 가 결정 (아래 render).
            if (ap.size_table && typeof ap.size_table === 'object') {
              setSizeTable(ap.size_table);
            } else {
              setSizeTable(null);
            }
          } else {
            setActiveProduct(null);
            setSizeTable(null);
          }
          // STEP OVERLAY-2C · display/variants (optional · backward-compat)
          if (msg && msg.display && typeof msg.display === 'object') {
            setDisplay(msg.display);
          }
          if (Array.isArray(msg?.variants)) {
            setVariants(msg.variants);
          } else if (!ap) {
            setVariants([]);
          }
          // STEP OVERLAY-2J-QR · additive QR state on same active_product topic.
          // · visible=false → unmount · visible=true + url_path → render client-side.
          if (msg && Object.prototype.hasOwnProperty.call(msg, 'qr')) {
            qrRtTsRef.current = Date.now();
            const q = msg.qr;
            if (q && typeof q === 'object' && q.visible && q.url_path) {
              setQrState({ product_id: q.product_id || null,
                            url_path: q.url_path, expires_at: q.expires_at || null });
            } else {
              setQrState(null);
            }
          }
          return;
        }
        // --- timer (STEP OVERLAY-2D) ---
        if (t === 'timer' || msg?.type === 'timer') {
          const tm = msg && 'timer' in msg ? msg.timer : null;
          timerRtTsRef.current = Date.now();
          setTimer(tm && typeof tm === 'object' ? tm : null);
          return;
        }
        // --- social_proof (STEP OVERLAY-2G-FOLLOWER + future purchase) ---
        // Transient toast events — Queue 에 enqueue (persistent state 와 분리).
        if (t === 'social_proof' || msg?.type === 'social_proof') {
          try {
            const eid = msg?.proof_id || msg?.event_id || null;
            if (!eid) return;
            if (seenEventIdsRef.current.has(eid)) return;
            seenEventIdsRef.current.add(eid);
            // Set 가 지나치게 커지는 것을 막기 위해 가끔 비움 (dedupe window).
            if (seenEventIdsRef.current.size > 500) seenEventIdsRef.current = new Set([eid]);
            // STEP OVERLAY-2H-PURCHASE-WIRE · 서버가 duration_ms 를 payload 에
            // 포함하면 (follower vs purchase 설정 반영) 그 값을 우선 사용. 없거나
            // invalid 하면 renderer 기본값 fallback. 범위 clamp 는 dequeue 단계에서
            // 이미 처리 (`Math.max(1500, Math.min(10000, ...))`).
            const _pdur = parseInt(msg?.duration_ms, 10);
            const ev = {
              event_id:  eid,
              event_type: msg?.event || 'social_proof',
              text:       msg?.text || '',
              masked_name: msg?.masked_name || '',
              created_at: msg?.ts || Date.now(),
              duration_ms: Number.isFinite(_pdur) && _pdur > 0
                ? _pdur : EVENT_DEFAULT_DURATION_MS,
            };
            setEventQueue((q) => {
              const next = [...q, ev];
              return next.length > EVENT_QUEUE_MAX
                ? next.slice(next.length - EVENT_QUEUE_MAX) : next;
            });
          } catch (_e) { /* Failure Isolation */ }
          return;
        }
        // --- notice (STEP OVERLAY-2B-NOTICE) ---
        if (t === 'notice' || msg?.type === 'notice') {
          const n = msg && 'notice' in msg ? msg.notice : null;
          noticeRtTsRef.current = Date.now();
          if (n && typeof n === 'object' && (n.text || '').toString().trim()) {
            setNotice({ text: String(n.text).trim(),
                        updated_at: n.updated_at || null });
          } else {
            setNotice(null);
          }
          return;
        }
        // 기존 chat / pin / social_proof 등 다른 topic 은 silent ignore.
      } catch (_e) {
        // Failure Isolation — 어떤 payload 오류도 Renderer 를 crash 시키지 않는다.
      }
    };
    room.on(RoomEvent.TrackSubscribed, onTrackSubscribed);
    room.on(RoomEvent.TrackUnsubscribed, onTrackUnsubscribed);
    room.on(RoomEvent.DataReceived, onDataReceived);
    (async () => {
      try {
        await room.connect(lkUrl, lkToken);
        if (!mountedRef.current) return;
        try { await room.startAudio(); } catch (_e) { /* autoplay 허용 환경(Egress Chrome) · 실패 무시 */ }
        setConnected(true);
      } catch (_e) {
        // Overlay Failure Isolation — 영상 연결 실패 시에도 crash X.
        setConnected(false);
      }
    })();
    return () => {
      mountedRef.current = false;
      try { room.disconnect(); } catch (_e) { /* ignore */ }
      roomRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Active Product polling (REUSE /public/.../live-context) ──────────────
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    let timerId = null;
    const url = `${apiBase}/api/public/streaming/sessions/${encodeURIComponent(sessionId)}/live-context`;
    const tick = async () => {
      try {
        let data;
        if (parentMode) {
          data = parentCtxRef.current;
          if (!data) return;
        } else {
          const resp = await fetch(url, { credentials: 'omit' });
          if (!resp.ok) throw new Error(`ctx_${resp.status}`);
          data = await resp.json();
        }
        if (cancelled) return;
        // STEP OVERLAY-2B-RT · active_product RT TTL guard.
        if (Date.now() - rtLastTsRef.current >= RT_PRIORITY_TTL_MS) {
          const ap = data && data.active_product ? data.active_product : null;
          setActiveProduct(ap && typeof ap === 'object' ? ap : null);
          // STEP OVERLAY-SIZE-TABLE-1 · polling recovery for size_table.
          if (ap && ap.size_table && typeof ap.size_table === 'object') {
            setSizeTable(ap.size_table);
          } else {
            setSizeTable(null);
          }
          // STEP OVERLAY-2C · polling recovery for display/variants.
          const dsp = data && data.active_product_display;
          if (dsp && typeof dsp === 'object') setDisplay(dsp);
          setLayout(data && data.overlay_layout && typeof data.overlay_layout === 'object' ? data.overlay_layout : null);
          const avs = data && data.active_product_variants;
          if (Array.isArray(avs)) setVariants(avs);
          else if (!ap) setVariants([]);
          // STEP OVERLAY-2J-QR · polling recovery for active_product_qr.
          // 서버가 이미 expired/HIDE 를 null 로 자른 응답을 돌려주므로 그대로 사용.
          const qrp = data && data.active_product_qr;
          qrRtTsRef.current = qrRtTsRef.current;   // keep ref (updated on RT only)
          if (Date.now() - qrRtTsRef.current >= RT_PRIORITY_TTL_MS) {
            if (qrp && typeof qrp === 'object' && qrp.visible) {
              setQrState({ product_id: qrp.product_id || null,
                            url_path: qrp.url_path || null,
                            expires_at: qrp.expires_at || null });
            } else {
              setQrState(null);
            }
          }
        }
        // STEP OVERLAY-2B-NOTICE · notice RT TTL guard (독립 TTL).
        {
          const en = data && data.external_notice ? data.external_notice : null;
          setExtNotice((prev) => (JSON.stringify(prev) === JSON.stringify(en) ? prev : en));
        }
        if (Date.now() - noticeRtTsRef.current >= RT_PRIORITY_TTL_MS) {
          const nv = data && data.live_notice ? data.live_notice : null;
          if (nv && typeof nv === 'object' && (nv.text || '').toString().trim()) {
            setNotice({ text: String(nv.text).trim(),
                        updated_at: nv.updated_at || null });
          } else {
            setNotice(null);
          }
        }
        // STEP OVERLAY-2D · timer polling recovery (독립 TTL).
        if (Date.now() - timerRtTsRef.current >= RT_PRIORITY_TTL_MS) {
          const tm = data && data.timer ? data.timer : null;
          setTimer(tm && typeof tm === 'object' ? tm : null);
        }
      } catch (_e) {
        // Overlay Failure Isolation — fetch 실패 시 state 유지 (유령 깜빡임 방지).
      }
    };
    tick();
    timerId = setInterval(tick, CTX_POLL_MS);
    return () => { cancelled = true; if (timerId) clearInterval(timerId); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, apiBase, parentTick]);

  const isPortrait = orientation === 'portrait';

  // STEP OVERLAY-2D-TIMER · 1Hz local tick (clock drift 방지 · setInterval 횟수 미사용).
  // running 상태인 timer 가 있을 때만 활성화 (불필요 re-render 방지).
  useEffect(() => {
    if (!timer || timer.status !== 'running' || !timer.visible) return;
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, [timer]);

  // STEP OVERLAY-2G-FOLLOWER · Event Queue dispatcher (FIFO · auto-disappear).
  useEffect(() => {
    if (currentEvent || eventQueue.length === 0) return;
    const [next, ...rest] = eventQueue;
    setEventQueue(rest);
    setCurrentEvent(next);
    const dur = Math.max(1500, Math.min(10000, parseInt(next.duration_ms || EVENT_DEFAULT_DURATION_MS, 10)));
    const tid = setTimeout(() => setCurrentEvent(null), dur);
    return () => clearTimeout(tid);
  }, [currentEvent, eventQueue]);

  // STEP OVERLAY-2J-QR · auto-hide once expires_at passes · local timer only (no DB write).
  useEffect(() => {
    if (!qrState || !qrState.expires_at) return;
    let exp;
    try {
      const s = qrState.expires_at.endsWith('Z') ? qrState.expires_at
                : qrState.expires_at.includes('+') || qrState.expires_at.includes('-', 10)
                    ? qrState.expires_at : qrState.expires_at + 'Z';
      exp = new Date(s).getTime();
    } catch (_e) { return; }
    const delta = exp - Date.now();
    if (delta <= 0) { setQrState(null); return; }
    const tid = setTimeout(() => setQrState(null), Math.min(delta, 60_000));
    return () => clearTimeout(tid);
  }, [qrState]);

  // Timer display calc — running 은 end_at 기반, 그 외는 remaining_seconds.
  const timerView = (() => {
    if (!timer || !timer.visible || timer.status === 'idle' || timer.status === 'hidden') return null;
    let remaining = 0;
    if (timer.status === 'running' && timer.end_at) {
      const end = Date.parse(timer.end_at);
      if (!Number.isNaN(end)) remaining = Math.max(0, Math.ceil((end - nowTick) / 1000));
    } else if (timer.status === 'paused' || timer.status === 'finished') {
      remaining = Math.max(0, parseInt(timer.remaining_seconds || 0, 10));
    }
    if (remaining <= 0 && timer.status === 'running') remaining = 0;    // clamp
    const mm = String(Math.floor(remaining / 60)).padStart(2, '0');
    const ss = String(remaining % 60).padStart(2, '0');
    return {
      label: (timer.label || '타이머'),
      display: `${mm}:${ss}`,
      status: remaining <= 0 ? 'finished' : timer.status,
    };
  })();

  // ── Canvas style (9:16 or 16:9) ──────────────────────────────────────────
  // Egress Chromium 은 전체 뷰포트를 캔버스로 캡쳐한다.
  // 상대 좌표만 사용 → absolute hard-coded px 남발 금지 (OVERLAY-2A 지시사항 §9).
  const stageStyle = {
    position: 'fixed', inset: 0,
    background: preview ? 'transparent' : '#000',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    overflow: 'hidden',
    pointerEvents: preview ? 'none' : 'auto',
  };
  const videoStyle = {
    width: '100%', height: '100%', objectFit: 'cover',
    background: '#000',
    display: preview ? 'none' : 'block',
  };
  // STEP OVERLAY-UX-FINAL-2 · Portrait 9:16 Safe Area.
  // 하단 YouTube 채팅/플랫폼 UI 와 겹치지 않도록 bottom 20% 를 Safe Zone 으로
  // 비워 둔다 (최하단 ~20% 영역에는 Overlay 를 두지 않음). side 는 5%.
  // Position preset (`display.position`) 로 안전 영역 내에서 top/middle/bottom
  // 중 선택.  Egress + Seller Preview 가 동일 좌표를 공유 (REUSE).
  const SAFE = isPortrait
    ? { side: '5%', topArea: '12%', middleArea: '38%', bottomArea: '20%' }
    : { side: '3.5%', topArea: '8%', middleArea: '38%', bottomArea: '12%' };
  const position = (display && display.position) || 'bottom';
  const overlayWrapStyle = (() => {
    const base = { position: 'absolute', left: SAFE.side, right: SAFE.side,
                     display: 'flex', justifyContent: 'center', pointerEvents: 'none' };
    if (isPortrait) {
      if (position === 'top')    return { ...base, top: SAFE.topArea };
      if (position === 'middle') return { ...base, top: SAFE.middleArea };
      return { ...base, bottom: SAFE.bottomArea };    // default: bottom-safe
    }
    // landscape — 좌하단 유지 · 중앙 가리지 않음
    const lbase = { position: 'absolute', left: SAFE.side, maxWidth: '46%',
                     pointerEvents: 'none' };
    if (position === 'top')    return { ...lbase, top: SAFE.topArea };
    if (position === 'middle') return { ...lbase, top: '50%',
                                            transform: 'translateY(-50%)' };
    return { ...lbase, bottom: SAFE.bottomArea };
  })();

  // ── Product proof card ── STEP OVERLAY-UX-FINAL-2 · responsive sizing ──
  // 기본 (default) 은 Portrait 9:16 에서 가로폭 ~70% 를 차지하도록 vw 기준 치수.
  // mini < default < emphasized 의 시각 차이가 육안으로 명확하도록 scale/
  // 패딩/썸네일을 함께 변경 (단일 transform scale 로는 부족).
  // STEP OVERLAY-PRODUCT-UI-FINAL · REMAINING GAP FIX · 기본 상품 카드.
  // · background 완전 투명 — 영상이 그대로 보이도록 (지시 §3).
  // · 얇은 red accent border + 텍스트 섀도우로만 가독성 확보.
  // · emphasized 는 더 두꺼운 red border + glow (아래 L902 분기 유지).
  // · 사이즈표/공지/옵션 알약 배경은 이번 STEP 에서 변경하지 않는다.
  const cardStyle = {
    display: 'flex', alignItems: 'center',
    gap: isPortrait ? '2.6vw' : 12,
    padding: isPortrait ? '2.8vw 3.2vw' : '12px 14px',
    background: 'transparent',
    border: '1px solid rgba(255, 79, 61, 0.42)',
    borderRadius: isPortrait ? '3.6vw' : 18,
    color: '#fff',
    maxWidth: '100%',
  };
  // 투명 배경에서도 가독성 유지를 위한 최소 텍스트 섀도우 (지시 §4).
  const textShadow = '0 1px 2px rgba(0,0,0,0.85), 0 0 6px rgba(0,0,0,0.55)';
  const thumbStyle = {
    width: isPortrait ? '18vw' : 60,
    height: isPortrait ? '18vw' : 60,
    borderRadius: isPortrait ? '2.4vw' : 12,
    objectFit: 'cover', flexShrink: 0,
    background: '#222',
  };
  const nameStyle = {
    fontSize: isPortrait ? '3.6vw' : 13,
    fontWeight: 800, lineHeight: 1.25,
    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textShadow,
  };
  const priceStyle = {
    marginTop: 4,
    fontSize: isPortrait ? '5.0vw' : 15,
    fontWeight: 900,
    color: '#FF4F3D',
    textShadow,
  };

  // Overlay render guard — 모든 필수 필드가 갖춰진 Active Product 만 렌더.
  // 하나라도 비면 hide (Failure Isolation).
  // STEP OVERLAY-2C · display.mode='hidden' 이면 Product Overlay 자체 unmount.
  const mode = (display && display.mode) || 'default';
  const show = {
    image: !display || display.show_image !== false,
    name:  !display || display.show_name  !== false,
    price: !display || display.show_price !== false,
    options:      !!(display && display.show_options),
    size:         !!(display && display.show_size),
    optionPrice:  !!(display && display.show_option_price),
    // STEP OVERLAY-EGRESS-PRESET-ALIGN-1 · 전체정보형 Overlay 배송정보 flag.
    //   · 신규 Overlay 엔진 0 · 신규 preset 0 · build_display_state 가 preset
    //     (full) 에서 True 를 내려주고 downgrade 가 실 shipping_fee 없을 때 False.
    shipping:     !!(display && display.show_shipping),
  };
  const isMini = mode === 'mini';
  const isEmphasized = mode === 'emphasized';
  const canRender = !!(
    mode !== 'hidden'
    && activeProduct
    && activeProduct.product_id
    && ((activeProduct.name || '').toString().trim() || show.image)
  );

  // STEP OVERLAY-2B-NOTICE · Notice layer (Product 와 별도 영역 · 상단 중앙).
  // 이번 STEP 은 최소 Proof UI (label "공지" + 본문 1~2 lines).
  // 향후 상태 추가 가능: expanded / compact / emphasized / hidden
  //   → `data-notice-state` 로 Boundary 유지 (지금은 'expanded' 1종만 렌더).
  // REAL-CONTROL-INTEGRATION-1 · 외부공지 선택 시 기존 공지 위치·크기 설정을 재사용해 외부공지만 표시 (이미지 비율 유지 · contain).
  const notice = extNotice
    ? (extNotice.type === 'image' ? { text: '', image_url: extNotice.url, image_size: 'medium', external: true }
                                  : { text: extNotice.text || '', external: true })
    : selfNotice;
  const noticeState = notice ? 'expanded' : 'hidden';
  const _ok = orientKey(isPortrait);
  const _noticeRect = getRect(layout, _ok, 'notice_image');
  const _noticeFreeImg = !!(notice && notice.image_url && _noticeRect);
  const _noticeTextRect = getRect(layout, _ok, 'notice');
  const _timerRect = getRect(layout, _ok, 'timer');
  const canRenderNotice = !!(notice && (
    (notice.text || '').toString().trim() || (notice.image_url && !_noticeRect)));
  // 상단 Safe Area · Product Overlay 영역과 겹치지 않도록 top 쪽 사용.
  const noticeWrapStyle = isPortrait
    ? { position: 'absolute', top: '6%', left: '5%', right: '5%',
         display: 'flex', justifyContent: 'center', pointerEvents: 'none' }
    : { position: 'absolute', top: '5%', left: '50%', transform: 'translateX(-50%)',
         maxWidth: '60%', pointerEvents: 'none' };
  const noticeCardStyle = {
    display: 'flex', alignItems: 'flex-start', gap: 10,
    padding: isPortrait ? '12px 14px' : '10px 14px',
    background: LIGHT.bg,
    border: LIGHT.border,
    borderRadius: 14,
    backdropFilter: 'blur(14px)',
    WebkitBackdropFilter: 'blur(14px)',
    color: LIGHT.text,
    maxWidth: '100%',
    boxShadow: LIGHT.shadow,
  };
  const noticeLabelStyle = {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '2px 8px', borderRadius: 999,
    background: 'rgba(255, 59, 48, 0.12)',
    color: LIGHT.accent, fontSize: isPortrait ? 11 : 10, fontWeight: 900,
    flexShrink: 0, lineHeight: 1.2,
  };
  const noticeTextStyle = {
    fontSize: isPortrait ? 14 : 12, fontWeight: 700, lineHeight: 1.35,
    color: LIGHT.text, whiteSpace: 'pre-line',
    display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  };

  return (
    <div style={stageStyle} data-testid="egress-template-root"
         data-orientation={orientation}
         data-connected={connected ? '1' : '0'}>
      <video ref={videoRef} style={videoStyle} autoPlay playsInline muted
             data-testid="egress-template-video" />
      <div ref={audioBoxRef} style={{ display: 'none' }} data-testid="egress-template-audio-box" />
      {/* STUDIO-NOTICE-TIMER-LAYOUT-FIX-1 · 저장된 공지 문구 위치가 있으면 판매자 Studio 와 동일 rect · 없으면 기존 고정 위치. */}
      {canRenderNotice && _noticeTextRect && (
        <OverlayFreeBox rect={_noticeTextRect} base={baseFrac(_ok, 'notice', layout)} fluid testid="egress-overlay-free-notice">
          {(cw) => <OverlayNoticeCard free notice={notice} isPortrait={isPortrait} showImage={!_noticeRect} testidPrefix="egress"
                                      stageW={cw / (_noticeTextRect.w || 1)} boxW={cw} />}
        </OverlayFreeBox>
      )}
      {canRenderNotice && !_noticeTextRect && (
        <div style={noticeWrapStyle} data-testid="egress-overlay-notice-wrap"
             data-notice-state={noticeState}>
          <div style={noticeCardStyle}
               data-testid="egress-overlay-notice-card">
            {!(notice.external && !notice.text) && (
              <span style={noticeLabelStyle}
                    data-testid="egress-overlay-notice-label">📢 공지</span>
            )}
            <div style={{display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0, flex: 1}}>
              {notice.text && (
                <div style={noticeTextStyle}
                     data-testid="egress-overlay-notice-text">
                  {notice.text}
                </div>
              )}
              {/* STEP OVERLAY-REAL-UI-POLISH · Notice 이미지 additive.
                  object-fit: contain → 원본 비율 유지 (지시 §12).
                  small/medium/large preset · 9:16 세로 overflow 방지 (max-height). */}
              {notice.image_url && !_noticeRect && (() => {
                const sz = notice.image_size || 'medium';
                const maxH = isPortrait
                  ? (sz === 'small' ? 90 : sz === 'large' ? 240 : 150)
                  : (sz === 'small' ? 70 : sz === 'large' ? 180 : 120);
                const maxW = isPortrait
                  ? (sz === 'small' ? 140 : sz === 'large' ? 320 : 220)
                  : (sz === 'small' ? 120 : sz === 'large' ? 280 : 180);
                return (
                  <img src={notice.image_url}
                       alt=""
                       onError={(e) => { try { e.currentTarget.style.display = 'none'; } catch {} }}
                       style={{
                         display: 'block',
                         maxWidth: maxW, maxHeight: maxH,
                         width: 'auto', height: 'auto',
                         objectFit: 'contain',
                         borderRadius: 8,
                         background: 'rgba(255,255,255,0.05)',
                       }}
                       data-testid="egress-overlay-notice-image"
                       data-notice-image-size={sz} />
                );
              })()}
            </div>
          </div>
        </div>
      )}
      {_noticeFreeImg && (
        <OverlayFreeBox rect={_noticeRect} base={baseFrac(_ok, 'notice_image', layout)} testid="egress-overlay-free-notice-image">
          <OverlayNoticeImage src={notice.image_url} />
        </OverlayFreeBox>
      )}
      {/* STEP OVERLAY-2D-TIMER · event countdown · 중앙 오른쪽 safe area */}
      {/* STUDIO-NOTICE-TIMER-LAYOUT-FIX-1 · 저장된 타이머 위치·크기 반영 (Studio 와 동일 카드 · 00:00 표시 정책 유지 = keepFinished). */}
      {timerView && _timerRect && (
        <OverlayFreeBox rect={_timerRect} base={baseFrac(_ok, 'timer', layout)} fluid testid="egress-overlay-free-timer">
          <OverlayTimerCard free keepFinished isPortrait={isPortrait} viewOf={timerViewOf} timer={timer} testidPrefix="egress" />
        </OverlayFreeBox>
      )}
      {timerView && !_timerRect && (
        <div style={{
               position: 'absolute',
               ...(isPortrait
                   ? { top: '42%', right: '5%' }
                   : { top: '50%', right: '3.5%', transform: 'translateY(-50%)' }),
               pointerEvents: 'none',
             }}
             data-testid="egress-overlay-timer-wrap"
             data-timer-status={timerView.status}>
          <div style={{
                 display: 'flex', flexDirection: 'column', alignItems: 'flex-end',
                 gap: 4,
                 padding: isPortrait ? '10px 14px' : '10px 14px',
                 background: timerView.status === 'finished'
                   ? 'rgba(255, 79, 61, 0.9)'
                   : (timerView.status === 'paused' ? LIGHT.bgPaused : LIGHT.bg),
                 border: LIGHT.border,
                 borderRadius: 14,
                 backdropFilter: 'blur(14px)',
                 WebkitBackdropFilter: 'blur(14px)',
                 color: timerView.status === 'finished' ? '#fff' : LIGHT.text,
                 boxShadow: LIGHT.shadow,
                 minWidth: isPortrait ? 110 : 130,
               }}
               data-testid="egress-overlay-timer-card">
            <div style={{
                   fontSize: isPortrait ? 11 : 10, fontWeight: 900,
                   opacity: 0.9, letterSpacing: 0.5,
                   maxWidth: isPortrait ? '28vw' : 180,
                   overflow: 'hidden', textOverflow: 'ellipsis',
                   whiteSpace: 'nowrap',
                 }}
                 data-testid="egress-overlay-timer-label">
              ⏱ {timerView.label}
            </div>
            <div style={{
                   fontSize: isPortrait ? 32 : 28, fontWeight: 900,
                   lineHeight: 1.0, fontFamily: 'monospace',
                   letterSpacing: 1,
                 }}
                 data-testid="egress-overlay-timer-value">
              {timerView.display}
            </div>
            {timerView.status === 'paused' && (
              <div style={{fontSize: 10, fontWeight: 800, opacity: 0.75}}
                   data-testid="egress-overlay-timer-paused">⏸ 일시정지</div>
            )}
          </div>
        </div>
      )}

      {/* STEP OVERLAY-2G-FOLLOWER · Common Event Alert (transient · left-middle safe area)
          좌표는 REAL 방송 테스트 전 임시 Safe Area · OVERLAY-LAYOUT-TUNE 에서 최종 조정. */}
      {currentEvent && (
        <div style={{
               position: 'absolute',
               ...(isPortrait
                   ? { top: '22%', left: '5%', right: '5%' }
                   : { top: '18%', left: '3.5%', maxWidth: '38%' }),
               pointerEvents: 'none',
               animation: 'eg-event-fade 350ms ease-out',
             }}
             data-testid="egress-overlay-event-wrap"
             data-event-type={currentEvent.event_type}
             data-event-id={currentEvent.event_id}>
          <div style={{
                 display: 'flex', alignItems: 'center', gap: 10,
                 padding: '10px 14px',
                 background: currentEvent.event_type === 'follower_new'
                   ? 'rgba(255, 79, 125, 0.88)'
                   : (currentEvent.event_type === 'order_created'
                       || currentEvent.event_type === 'comment_order_confirmed')
                       // STEP OVERLAY-2H-PURCHASE-WIRE · 구매 알림 (gold-green)
                       // · 전용 카드 생성 금지 (지시 §18) · 같은 레이어 · 색만 분기.
                       ? 'rgba(16, 161, 90, 0.9)'
                       : currentEvent.event_type === 'inventory_sold_out'
                           // STEP OVERLAY-2I-INVENTORY · SOLD OUT (dark red)
                           ? 'rgba(176, 20, 20, 0.92)'
                           : currentEvent.event_type === 'inventory_low_stock'
                               // STEP OVERLAY-2I-INVENTORY · 품절 임박 (orange)
                               ? 'rgba(242, 122, 24, 0.92)'
                               : 'rgba(10, 10, 10, 0.82)',
                 border: '1px solid rgba(255, 255, 255, 0.1)',
                 borderRadius: 14,
                 backdropFilter: 'blur(14px)',
                 WebkitBackdropFilter: 'blur(14px)',
                 color: '#fff',
                 boxShadow: '0 10px 32px rgba(0, 0, 0, 0.35)',
                 maxWidth: '100%',
               }}
               data-testid="egress-overlay-event-card">
            <div style={{
                   fontSize: isPortrait ? 14 : 12, fontWeight: 900, lineHeight: 1.3,
                   display: '-webkit-box', WebkitLineClamp: 2,
                   WebkitBoxOrient: 'vertical', overflow: 'hidden',
                   wordBreak: 'break-word',
                 }}
                 data-testid="egress-overlay-event-text">
              {currentEvent.text}
            </div>
          </div>
        </div>
      )}
      <style>{`
        @keyframes eg-event-fade {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* STEP OVERLAY-2J-QR · Active product QR card (persistent until expires_at).
          · 서버는 url_path 만 전달 (예: /product/{pid}) · Renderer 가 origin 조합.
          · 임시 Safe Area · OVERLAY-LAYOUT-TUNE 에서 최종 조정 (지시 §27). */}
      {qrState && qrState.url_path && (() => {
        const origin = (typeof window !== 'undefined' && window.location && window.location.origin)
                        ? window.location.origin : '';
        const fullUrl = origin + qrState.url_path;
        const _qrRect = getRect(layout, _ok, 'qr');
        if (_qrRect) {
          return (
            <OverlayFreeBox rect={_qrRect} base={baseFrac(_ok, 'qr', layout)} testid="egress-overlay-free-qr">
              {(cw) => <OverlayQrCard url={fullUrl} size={Math.max(60, Math.round(cw - 24))} isPortrait={isPortrait} />}
            </OverlayFreeBox>
          );
        }
        // URL-encode via Google chart replacement · library import 없이도 image fallback.
        // 메인 경로: qrcode library (ShareModal 이 이미 사용 중) 를 dynamic import.
        return (
          <div style={{
                 position: 'absolute',
                 ...(isPortrait
                     ? { right: '5%', bottom: '18%' }
                     : { right: '3%', bottom: '15%' }),
                 pointerEvents: 'none',
                 animation: 'eg-event-fade 320ms ease-out',
               }}
               data-testid="egress-overlay-qr-wrap"
               data-qr-product-id={qrState.product_id || ''}>
            <OverlayQrCard url={fullUrl} size={isPortrait ? 220 : 170} isPortrait={isPortrait} />
          </div>
        );
      })()}

      {/* STEP OVERLAY-SIZE-TABLE-1 · 상품 상세 사이즈표 Overlay.
          display.size_table_visible === true 이고 상품에 size_table 데이터가 있을 때만.
          Safe Area 중앙 상단 · position preset 과 공존 (상품 카드 영역과 겹치지 않음).
          기존 Overlay engine REUSE (별도 realtime/polling 없음).
          max-height · max-width 로 영상 전체를 덮지 않도록 clamp. */}
      {(() => {
        if (!display || display.size_table_visible !== true) return null;
        if (!sizeTable || typeof sizeTable !== 'object') return null;
        const cols = Array.isArray(sizeTable.columns) ? sizeTable.columns : [];
        const rows = Array.isArray(sizeTable.rows) ? sizeTable.rows : [];
        const guide = (sizeTable.guide_text || '').toString().trim();
        if (!cols.length && !rows.length && !guide) return null;
        const _stRect = getRect(layout, _ok, 'size_table');
        if (_stRect && sizeTableHasData(sizeTable)) {
          return (
            <OverlayFreeBox rect={_stRect} base={baseFrac(_ok, 'size_table', layout)} testid="egress-overlay-free-size-table">
              <OverlaySizeTableCard sizeTable={sizeTable} isPortrait={isPortrait} free />
            </OverlayFreeBox>
          );
        }
        return (
          <div style={{
                 position: 'absolute',
                 ...(isPortrait
                     ? { top: '14%', left: '5%', right: '5%' }
                     : { top: '50%', left: '50%',
                          transform: 'translate(-50%, -50%)',
                          maxWidth: '70%' }),
                 pointerEvents: 'none',
                 animation: 'eg-event-fade 320ms ease-out',
               }}
               data-testid="egress-overlay-size-table-wrap">
            <div style={{
                   display: 'flex', flexDirection: 'column', gap: 8,
                   padding: isPortrait ? '14px 14px' : '14px 18px',
                   background: LIGHT.bg,
                   border: LIGHT.border,
                   borderRadius: 14,
                   backdropFilter: 'blur(14px)',
                   WebkitBackdropFilter: 'blur(14px)',
                   color: LIGHT.text,
                   maxWidth: '100%',
                   maxHeight: isPortrait ? '58vh' : '70vh',
                   overflow: 'hidden',
                   boxShadow: LIGHT.shadow,
                 }}
                 data-testid="egress-overlay-size-table-card">
              <div style={{
                     display: 'inline-flex', alignItems: 'center', gap: 6,
                     fontSize: isPortrait ? 12 : 11, fontWeight: 900,
                     letterSpacing: 0.4, color: LIGHT.accent,
                   }}
                   data-testid="egress-overlay-size-table-label">
                📏 사이즈 안내
              </div>
              {(cols.length > 0 && rows.length > 0) && (
                <div style={{ overflow: 'auto', maxHeight: isPortrait ? '38vh' : '50vh' }}>
                  <table style={{
                           width: '100%', borderCollapse: 'collapse',
                           fontSize: isPortrait ? '3.4vw' : 14,
                           lineHeight: 1.25,
                         }}
                         data-testid="egress-overlay-size-table-grid">
                    <thead>
                      <tr>
                        {cols.map((c, ci) => (
                          <th key={ci}
                              style={{
                                padding: isPortrait ? '6px 4px' : '8px 10px',
                                borderBottom: `1px solid ${LIGHT.line}`,
                                fontWeight: 900, textAlign: 'center',
                                color: LIGHT.text, whiteSpace: 'nowrap',
                              }}>
                            {c}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, ri) => (
                        <tr key={ri}>
                          {cols.map((_, ci) => (
                            <td key={ci}
                                style={{
                                  padding: isPortrait ? '5px 4px' : '7px 10px',
                                  borderBottom: `1px solid ${LIGHT.lineSoft}`,
                                  textAlign: 'center',
                                  color: LIGHT.text,
                                  fontWeight: ci === 0 ? 800 : 600,
                                }}>
                              {(r && r[ci]) || ''}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {guide && (
                <div style={{
                       fontSize: isPortrait ? 12 : 11, fontWeight: 700,
                       lineHeight: 1.35, color: LIGHT.sub,
                       borderTop: `1px solid ${LIGHT.line}`,
                       paddingTop: 6, whiteSpace: 'pre-line',
                     }}
                     data-testid="egress-overlay-size-table-guide">
                  {guide}
                </div>
              )}
            </div>
          </div>
        );
      })()}
      {canRender && (() => {
        const _cardRect = getRect(layout, _ok, 'card');
        const _vert = !!_cardRect && cardOrientation(layout) === 'vertical';
        const cardNode = (
          <div style={{
                 ...cardStyle,
                 ...(_cardRect ? { width: '100%', boxSizing: 'border-box' } : {}),
                 ...(_vert ? { flexDirection: 'column', alignItems: 'center', textAlign: 'center' } : {}),
                 // STEP OVERLAY-UX-FINAL-2 · mini / emphasized 명확한 시각 차이.
                 // mini: 전체 ~62% scale · emphasized: ~1.22 scale + 강조 테두리 + glow.
                 transform: isMini ? 'scale(0.62)'
                                     : (isEmphasized ? 'scale(1.22)' : 'none'),
                 transformOrigin: position === 'top' ? 'top center'
                                   : (position === 'middle' ? 'center' : 'bottom center'),
                 border: isEmphasized ? '2px solid #FF4F3D' : cardStyle.border,
                 boxShadow: isEmphasized
                   ? '0 10px 32px rgba(0,0,0,0.35), 0 0 0 4px rgba(255,79,61,0.25)'
                   : 'none',
               }}
               data-testid="egress-overlay-product-card"
               data-product-id={activeProduct.product_id}
               data-preset={(display && display.preset) || 'default'}>
            {show.image && (
              <SafeImg src={activeProduct.image || ''}
                       alt={activeProduct.name || ''}
                       style={_vert ? { ...thumbStyle, width: '100%', height: 'auto', aspectRatio: '1 / 1' } : thumbStyle}
                       data-testid="egress-overlay-product-image" />
            )}
            <div style={{ minWidth: 0, flex: 1, ...(_vert ? { width: '100%' } : {}) }}>
              {show.name && (
                <div style={_vert ? { ...nameStyle, WebkitLineClamp: 3 } : nameStyle}
                     data-testid="egress-overlay-product-name">
                  {activeProduct.name}
                </div>
              )}
              {show.price && Number.isFinite(Number(activeProduct.sale_price)) && (
                <div style={priceStyle}
                     data-testid="egress-overlay-product-price">
                  {_won(activeProduct.sale_price)}
                </div>
              )}
              {/* STEP OVERLAY-EGRESS-PRESET-ALIGN-1 · 전체정보형 (preset=full) Overlay 의
                  실 배송정보 라인 — product.shipping_fee 가 null 이면 라인 자체 미표시
                  (fabrication 금지 · 자사라이브 LivePresetOverlay 와 동일 contract).
                  · 0 → '무료배송' · > 0 → '배송비 N원' · null/NaN/mini → render skip. */}
              {show.shipping && !isMini && (() => {
                const raw = activeProduct.shipping_fee;
                if (raw === null || raw === undefined || raw === '') return null;
                const n = Number(raw);
                if (!Number.isFinite(n)) return null;
                const label = n <= 0 ? '무료배송'
                                      : `배송비 ${n.toLocaleString('ko-KR')}원`;
                return (
                  <div style={{
                         marginTop: 4,
                         fontSize: isPortrait ? '2.8vw' : 11,
                         fontWeight: 700, color: '#ddd',
                         textShadow,
                       }}
                       data-testid="egress-overlay-product-shipping"
                       data-shipping-fee={String(n)}>
                    {label}
                  </div>
                );
              })()}
              {/* STEP OVERLAY-2I-INVENTORY · Persistent inventory badge (product card 내부).
                  display.inventory_mode = hidden | exact | status.
                  · hidden   → 미표시 (default).
                  · exact    → "남은 수량 N개" (available_stock > 0).
                  · status   → "품절 임박" (low_stock) · "SOLD OUT" (sold_out).
                  unknown / unlimited 상태는 어떤 mode 에서도 badge 미표시 (Fabrication 금지). */}
              {(() => {
                const invMode = display && display.inventory_mode;
                if (!invMode || invMode === 'hidden' || isMini) return null;
                const status = activeProduct.stock_status;
                const avail  = activeProduct.available_stock;
                if (status === 'unknown' || status === 'unlimited') return null;
                let label = null, bg = 'rgba(255,255,255,0.12)', color = '#fff';
                if (invMode === 'exact' && Number.isFinite(Number(avail)) && Number(avail) > 0) {
                  label = `남은 수량 ${Number(avail).toLocaleString('ko-KR')}개`;
                  bg = 'rgba(255,255,255,0.14)';
                } else if (status === 'sold_out') {
                  label = 'SOLD OUT'; bg = 'rgba(176,20,20,0.95)'; color = '#fff';
                } else if (status === 'low_stock' && invMode === 'status') {
                  label = '품절 임박'; bg = 'rgba(242,122,24,0.95)'; color = '#fff';
                } else if (status === 'low_stock' && invMode === 'exact'
                             && Number.isFinite(Number(avail)) && Number(avail) > 0) {
                  // exact mode 에서도 low_stock 때 숫자만 노출. 임의 '임박' 텍스트는 status 전용.
                  label = `남은 수량 ${Number(avail).toLocaleString('ko-KR')}개`;
                  bg = 'rgba(242,122,24,0.85)';
                }
                if (!label) return null;
                return (
                  <div style={{
                         marginTop: 6, display: 'inline-flex',
                         alignItems: 'center', gap: 4,
                         padding: '3px 10px', borderRadius: 999,
                         background: bg, color: color,
                         fontSize: 11, fontWeight: 900, letterSpacing: 0.3,
                       }}
                       data-testid="egress-overlay-inventory-badge"
                       data-inventory-mode={invMode}
                       data-stock-status={status || ''}>
                    {label}
                  </div>
                );
              })()}
              {/* STEP OVERLAY-2C · 옵션 요약 (최대 6개 · overflow safe) */}
              {show.options && variants.length > 0 && !isMini && (
                <div style={{
                       marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 4,
                       maxWidth: '100%',
                     }}
                     data-testid="egress-overlay-product-options">
                  {variants.slice(0, 6).map((v, i) => {
                    const snap = v.option_snapshot || {};
                    const label = Object.values(snap).join(' · ') || v.variant_key || `#${i+1}`;
                    // STEP OVERLAY-EGRESS-PRESET-ALIGN-1 · 옵션형 preset 에서 variants[].sale_price
                    //   가 존재하면 자동 노출 (실 데이터 있을 때만 · fabrication 금지).
                    //   · show.optionPrice 가 명시적으로 True 이거나 (option_price preset)
                    //   · 혹은 show.options 활성 상태(options preset) + 해당 variant 가 가격 보유.
                    const _hasVP = v.sale_price !== null && v.sale_price !== undefined;
                    const showVP = _hasVP && (show.optionPrice || show.options);
                    return (
                      <span key={v.variant_key || i}
                            style={{
                              padding: '3px 8px', borderRadius: 999,
                              background: 'rgba(255,255,255,0.1)',
                              fontSize: 11, fontWeight: 700, color: '#fff',
                              whiteSpace: 'nowrap',
                            }}
                            data-testid={`egress-overlay-option-${i}`}>
                        {label}
                        {showVP ? ` · ${_won(v.sale_price)}` : ''}
                      </span>
                    );
                  })}
                  {variants.length > 6 && (
                    <span style={{fontSize: 11, color: '#aaa'}}
                          data-testid="egress-overlay-option-more">
                      +{variants.length - 6}
                    </span>
                  )}
                </div>
              )}
              {/* STEP OVERLAY-2C · 사이즈 요약 (option_snapshot 에서 '사이즈'/'size' 키 추출) */}
              {show.size && variants.length > 0 && !isMini && (
                <div style={{marginTop: 4, fontSize: 11, color: '#ddd', fontWeight: 700}}
                     data-testid="egress-overlay-product-sizes">
                  사이즈: {(() => {
                    const sizes = [];
                    for (const v of variants) {
                      const snap = v.option_snapshot || {};
                      for (const k of Object.keys(snap)) {
                        if (/사이즈|size/i.test(k)) {
                          const sv = snap[k];
                          if (sv && !sizes.includes(sv)) sizes.push(sv);
                        }
                      }
                    }
                    return sizes.slice(0, 8).join(' · ') || '-';
                  })()}
                </div>
              )}
            </div>
          </div>
        );
        if (_cardRect && _vert) {
          // STEP STUDIO-OVERLAY-RESPONSIVE-PERSIST-1 · 세로형은 Studio 와 동일 컴포넌트(반응형) 사용.
          return (
            <OverlayFreeBox rect={_cardRect} base={baseFrac(_ok, 'card', layout)} fluid testid="egress-overlay-free-card">
              <LivePresetOverlay preset={(display && display.preset) || 'default'} product={activeProduct} free orientation="vertical" />
            </OverlayFreeBox>
          );
        }
        if (_cardRect) {
          return (
            <OverlayFreeBox rect={_cardRect} base={baseFrac(_ok, 'card', layout)} testid="egress-overlay-free-card">
              {cardNode}
            </OverlayFreeBox>
          );
        }
        return (
          <div style={overlayWrapStyle} data-testid="egress-overlay-wrap"
               data-mode={mode}
               data-position={position}>
            {cardNode}
          </div>
        );
      })()}
      {/* STEP OVERLAY-UX-FINAL-2 · Seller Preview 전용 Safe Area 가이드.
          preview=1 일 때만 희미한 선으로 bottom safe-zone 을 표시.
          Egress 로는 송출되지 않음 (preview flag 기준). */}
      {preview && (
        <div style={{
               position: 'absolute',
               left: 0, right: 0,
               bottom: isPortrait ? '20%' : '12%',
               borderTop: '1px dashed rgba(255,79,61,0.35)',
               pointerEvents: 'none',
             }}
             data-testid="egress-preview-safe-area-guide" />
      )}
    </div>
  );
};

export default EgressTemplate;
